import type AiCostsFilterView from '@UI/Pages/Administration/AiCosts/Filters/AiCostsFilterView';
import type AiCostFilterOptionsView from '@UI/Store/AiCosts/Views/AiCostFilterOptionsView';
import type AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';
import type AiCostLimitsListView from '@UI/Store/AiCostLimits/Views/AiCostLimitsListView';

/**
 * The limit that bounds the filtered scope, the first that exists in this order: the filtered user's,
 * the filtered facility's (or the facility of a filtered user who belongs to only one), the filtered
 * structure's, then the account's.
 */
const resolveApplicableLimit = (
	limits: AiCostLimitsListView,
	filters: AiCostsFilterView,
	options: AiCostFilterOptionsView | null,
): AiCostLimitView | null => {
	const { organizationId, facilityId, userId } = filters.hasScope
		? filters
		: { organizationId: null, facilityId: null, userId: null };

	const userFacilityIds = options?.users.find((user) => user.id === userId)?.facilityIds ?? [];
	const singleUserFacilityId = userFacilityIds.length === 1 ? (userFacilityIds[0] ?? null) : null;

	const candidates: ReadonlyArray<{ level: AiCostLimitView['level']; subjectId: number | null }> = [
		{ level: 'user', subjectId: userId },
		{ level: 'facility', subjectId: facilityId ?? singleUserFacilityId },
		{ level: 'organization', subjectId: organizationId },
	];

	for (const candidate of candidates) {
		const limit =
			candidate.subjectId === null ? null : limits.findBySubject(candidate.level, candidate.subjectId);

		if (limit !== null) {
			return limit;
		}
	}

	return limits.ofLevel('account')[0] ?? null;
};

export default resolveApplicableLimit;
